<template>
	<portal :to="portalTarget">
		<div
			class="unified-sheet-wrapper"
			:class="[
        `unified-sheet-wrapper--${layout.toLowerCase()}`
      ]"
			:data-open="state === 'open' ? 1 : 0"
			:style="wrapperStyle"
		>
			<div
				class="unified-sheet-bg"
				:class="bgClass"
				@click="handleBackgroundClick"
			></div>

			<div
				ref="sheet"
				class="unified-sheet"
				:class="[
          `unified-sheet--${layout.toLowerCase()}`,
          { ['size-'+size]: layout === 'Desktop' }
        ]"
				:data-state="isMove ? 'move' : state"
				:style="sheetStyle"
			>
				<!-- Mobile: Pan area for dragging -->
				<div v-if="layout === 'Mobile'" class="pan-area" ref="pan">
					<div class="bar" ref="bar"></div>
				</div>

				<!-- Desktop: Header with close button -->
				<div v-else class="modal-header">
					<button
						class="modal-close-btn"
						@click="close"
						aria-label="Close"
					>
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
							<path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="5" stroke-linecap="square"/>
						</svg>
					</button>
				</div>

				<div class="unified-sheet-contents" ref="contents">
					<slot></slot>
				</div>
			</div>
		</div>
	</portal>
</template>

<script>
import sheetManager from "../../../utils/sheet-manager";

export default {
	name: "BottomSheet",
	props: {
		openY: {
			type: Number,
			default: 0.1,
		},
		halfY: {
			type: Number,
			default: 0.8,
		},
		defaultState: {
			type: String,
			default: "close",
		},
		size: {
			type: String,
			default: "medium", // small | medium | large | full
			required: false,
			validator: value => ['small', 'medium', 'large', 'full'].includes(value)
		},
		stackBehavior: {
			type: String,
			default: "replace",
			validator: value => ['stack', 'replace'].includes(value)
		},
		enableStacking: {
			type: Boolean,
			default: false
		},
		preventBgClose: {
			type: Boolean,
			default: false
		},
	},
	data() {
		return {
			mc: null,
			y: 0,
			startY: 0,
			isMove: false,
			state: this.defaultState,
			rect: {},
			layout: "Mobile",
			sheetId: null,
			currentZIndex: 9999900,
			isRegistered: false,
			manager: null
		};
	},
	computed: {
		portalTarget() {
			return this.enableStacking ? 'bottom-sheet-stack' : 'bottom-sheet';
		},

		wrapperStyle() {
			const baseStyle = {
				zIndex: this.enableStacking ? this.currentZIndex : 9999900
			};
			return baseStyle;
		},

		bgClass() {
			if (!this.enableStacking) {
				return { 'unified-sheet-bg--show': this.state === 'open' };
			}
			return {
				'unified-sheet-bg--show': this.state === 'open' && this.isTopSheet,
				'unified-sheet-bg--hide': this.state === 'open' && !this.isTopSheet
			};
		},

		sheetStyle() {
			const baseStyle = {};

			if (this.layout === 'Mobile') {
				baseStyle.top = `${this.isMove ? this.y : this.calcY()}px`;
			}

			if (this.enableStacking) {
				baseStyle.zIndex = this.currentZIndex + 1;
			}

			return baseStyle;
		},

		isTopSheet() {
			return !this.enableStacking || !this.manager || this.manager.isTopSheet(this.sheetId);
		}
	},
	watch: {
		state(newState, oldState) {
			if (!this.enableStacking) return;

			if (newState === 'open' && oldState !== 'open') {
				this.handleOpen();
			} else if (newState === 'close' && oldState !== 'close') {
				this.handleClose();
			}
		}
	},
	mounted() {
		console.log('BottomSheet mounting...', {
			enableStacking: this.enableStacking,
			stackBehavior: this.stackBehavior
		});

		this.detectLayout();

		if (this.enableStacking) {
			console.log('Stacking enabled, initializing...');
			if (typeof sheetManager === 'undefined') {
				console.error('SheetManager not available!');
				return;
			}
			this.initializeStacking();
		}

		if (this.layout === "Mobile") {
			this.setupMobileInteractions();
		}

		this.$emit("has-mounted");
	},
	beforeDestroy() {
		window.onresize = null;

		if (this.enableStacking && this.isRegistered) {
			this.manager.unregister(this.sheetId);
		}
	},
	methods: {
		detectLayout() {
			this.layout = window.innerWidth < 1024 ? "Mobile" : "Desktop";

			window.onresize = () => {
				const newLayout = window.innerWidth < 1024 ? "Mobile" : "Desktop";
				if (newLayout !== this.layout) {
					this.layout = newLayout;
					if (this.layout === "Mobile") {
						this.$nextTick(() => {
							this.setupMobileInteractions();
						});
					}
				}
			};
		},

		setupMobileInteractions() {
			if (this.$refs.sheet) {
				this.rect = this.$refs.sheet.getBoundingClientRect();
				// If rect is invalid, set reasonable defaults
				if (!this.rect.height || this.rect.height === 0) {
					this.rect = {
						height: window.innerHeight +200,
						width: window.innerWidth,
						top: 0,
						left: 0
					};
				}

				this.recalculateHeight();
			}
		},

		/**
		 * Recalculate the sheet height and dimensions
		 * Call this method when the content changes or you need to refresh positioning
		 */
		recalculateHeight() {
			if (this.layout === "Mobile") {
				// Force a DOM refresh first
				this.$nextTick(() => {
					// Recalculate sheet dimensions
					if (this.$refs.sheet) {
						this.rect = this.$refs.sheet.getBoundingClientRect();

						// If rect is still invalid, use window dimensions
						if (!this.rect.height || this.rect.height === 0) {
							this.rect = {
								height: window.innerHeight + 200,
								width: window.innerWidth,
								top: 0,
								left: 0
							};
						}
					}

					// Force reactive update by triggering computed property recalculation
					this.$forceUpdate();

					// Emit event so parent can react to height changes
					this.$emit('height-recalculated', {
						rect: this.rect,
						calcY: this.calcY(),
						state: this.state
					});
				});
			} else {
				// For desktop, just force a reactive update
				this.$forceUpdate();
				this.$emit('height-recalculated', {
					layout: this.layout,
					state: this.state
				});
			}
		},

		close() {
			this.$emit("close");
		},

		calcY() {
			if (this.layout !== 'Mobile') return 0;

			let contentOffset = this.rect.height * this.openY;
			if (this.$refs.contents) {
				contentOffset = window.innerHeight -
					this.$refs.contents.getBoundingClientRect().height - 40;
			}

			switch (this.state) {
				case "close":
					return this.rect.height;
				case "open":
					return contentOffset;
				case "half":
					return this.rect.height * this.halfY;
				default:
					return this.y;
			}
		},

		setState(state) {
			this.state = state;
		},

		initializeStacking() {
			this.sheetId = this.generateSheetId();
			this.manager = sheetManager;
			this.manager.register(this.sheetId, this);
			this.isRegistered = true;
		},

		generateSheetId() {
			return `sheet-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
		},

		handleOpen() {
			if (this.enableStacking && this.manager) {
				this.manager.addToStack(this.sheetId, this.stackBehavior);
			}
		},

		handleClose() {
			if (this.enableStacking && this.manager) {
				this.manager.removeFromStack(this.sheetId);
			}
		},

		handleBackgroundClick() {

			if(this.preventBgClose){
				return;
			}

			if (!this.enableStacking) {
				this.close();
				return;
			}

			if (this.isTopSheet) {
				this.close();
			} else if (this.state === 'open') {
				this.bringToTop();
			}
		},

		updateZIndex(zIndex) {
			this.currentZIndex = zIndex;
		},

		forceClose() {
			this.state = 'close';
		},

		open() {
			this.state = 'open';
			this.$emit("open");
		},

		bringToTop() {
			if (this.enableStacking && this.manager && this.state === 'open') {
				this.manager.removeFromStack(this.sheetId);
				this.manager.addToStack(this.sheetId, 'stack');
			}
		}
	},
};
</script>

<style lang="scss" scoped>
.unified-sheet-wrapper {
	max-width: var(--app-width);

	&--mobile {
		z-index: 999999999999;

		&.unified-sheet-wrapper--open {
			position: fixed;
			top: 0;
			left: 0;
		}
	}

	&--desktop {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 999999999999;

		// Deposit button overrides — only apply when rendered inside this BottomSheet
		::v-deep #deposit-mpesa-btn {
			height: 56px !important;

			.btn {
				height: 56px !important;
				padding: 12px 16px !important;
				gap: 6px !important;
				border-radius: 8px !important;
				box-sizing: border-box !important;
			}

			.btntitle {
				font-size: 12px !important;
				font-weight: 600 !important;
			}

			img, svg {
				width: 18px !important;
				height: 18px !important;
			}
		}

		::v-deep #deposit-cashia-btn {
			.btn {
				height: 56px !important;
				padding: 12px 16px !important;
				gap: 6px !important;
				overflow: visible !important;
			}

			.btntitle {
				font-size: 12px !important;
				font-weight: 600 !important;
			}

			svg, img {
				width: 18px !important;
				height: 18px !important;
			}

			.cashia-badge {
				top: -6px !important;
				right: 0px !important;
			}
		}
	}
}

.unified-sheet-bg {
	background: transparent;
	transition: background 0.1s ease;
	z-index: 9999899;

	.unified-sheet-wrapper--mobile & {
		position: fixed;
		top: 0;
		left: 50%;
		right: 50%;
		bottom: 0;
		width: 100%;
		max-width: var(--app-width);
		transform: translateX(-50%);
	}

	.unified-sheet-wrapper--desktop & {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
	}

	&--show {
		background: rgba(0, 0, 0, 0.3);
	}

	&--hide {
		display: none;
		pointer-events: none;
	}
}

.unified-sheet {
	background: var(--white);
	position: relative;

	&--mobile {
		width: 100%;
		max-width: var(--app-width);
		transform: translateX(-50%);
		height: 100vh;
		position: fixed;
		box-shadow: 0 -3px 4px rgba(0, 0, 0, 0.1);
		left: 50%;
		z-index: 999999999998;

		&[data-state="half"],
		&[data-state="open"],
		&[data-state="close"] {
			transition: top 0.1s ease-out;
		}

		&[data-state="close"] {
			box-shadow: none;
		}
	}

	&--desktop {
		min-width: 600px;
		max-width: 90vw;
		max-height: 90vh;
		border-radius: 20px;
		box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
		0 10px 10px -5px rgba(0, 0, 0, 0.04);
		overflow: hidden;

		// Size variants
		&.size-small { min-width: 400px; max-width: 400px; z-index: 9999901;}
		&.size-medium { min-width: 550px; max-width: 600px; z-index: 9999901;}
		&.size-large {  min-width: 600px; max-width: 800px; z-index: 9999901;}
		&.size-full {  min-width:80vw; max-width: 95vw; z-index: 9999901;}
	}
}

.pan-area {
	padding: 12px 0;
}

.bar {
	width: 45px;
	height: 8px;
	border-radius: 14px;
	background: rgba(0, 0, 0, 0.3);
	margin: 0 auto;
	cursor: pointer;
}

.modal-header {
	padding: 16px 20px 0;
	display: flex;
	justify-content: flex-end;
}

.modal-close-btn {
	background: none;
	border: none;
	padding: 8px;
	cursor: pointer;
	border-radius: 6px;
	color: var(--text-secondary, #6b7280);
	transition: all 0.1s ease;

	&:hover {
		background: var(--gray-100, #f3f4f6);
		color: var(--text-primary, #111827);
	}
}

.unified-sheet-contents {
	overflow-y: auto;
	position: relative;

	.unified-sheet--mobile & {
		max-height: calc(100vh - 60px);
		box-sizing: border-box;
	}

	.unified-sheet--desktop & {
		max-height: calc(90vh - 60px);
		padding: 0 20px 20px;
	}
}

// Desktop modal entrance/exit animations
.unified-sheet-wrapper--desktop {
	&[data-open="1"] {
		animation: modalFadeIn 0.2s ease-out;

		.unified-sheet--desktop {
			animation: modalSlideIn 0.2s ease-out;
		}
	}
}

@keyframes modalFadeIn {
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
}

@keyframes modalSlideIn {
	from {
		transform: translateY(-20px) scale(0.95);
		opacity: 0;
	}
	to {
		transform: translateY(0) scale(1);
		opacity: 1;
	}
}
</style>
